import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import InviteMembersDropdown from './InviteMembersDropdown';
import {
  useGetCurrentUserQuery,
  useUpdateOrganizationMemberRolesMutation,
} from 'gql/graphql';
import { MemberOption } from 'features/user-groups/components/AddMembersDropdown/types';

export interface InviteTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (emails: string[]) => void;
  isGiveAccessDisabled?: boolean;
  platformTenantId?: string;
}

const InviteTeamModal: React.FC<InviteTeamModalProps> = ({
  isOpen,
  onClose,
  onSend,
  isGiveAccessDisabled = false,
  platformTenantId,
}) => {
  const [selectedMembers, setSelectedMembers] = useState<MemberOption[]>([]);
  const { data: currentUserData } = useGetCurrentUserQuery();
  const [updateRoles] = useUpdateOrganizationMemberRolesMutation();
  const toast = useToast();

  const handleSend = async () => {
    const baseEmails = selectedMembers.map((m) => m.email);
    const userEmail = currentUserData?.user?.email;
    const emails =
      userEmail && !baseEmails.includes(userEmail)
        ? [...baseEmails, userEmail]
        : baseEmails;

    if (platformTenantId) {
      const options = [...selectedMembers];
      const currentUserId = currentUserData?.user?.id;
      if (currentUserId && !options.some((o) => o.value === currentUserId)) {
        options.push({
          value: currentUserId,
          label: '',
          email: userEmail || '',
          icon: '',
        });
      }
      const userIds = options.map((o) => o.value);
      await Promise.all(
        userIds.map((userId) =>
          updateRoles({
            variables: {
              userId,
              applicationRolesToAdd: [
                {
                  productCode: 'XMCloud',
                  applicationId: platformTenantId,
                  roleName: 'Admin',
                },
              ],
              applicationRolesToDelete: [
                {
                  productCode: 'XMCloud',
                  applicationId: platformTenantId,
                  roleName: 'no-access',
                },
              ],
            },
          }).catch((err) => {
            toast({
              description: err.message,
              title: 'Error',
              status: 'error',
            });
          }),
        ),
      );
    }

    onSend(emails);
    const count = selectedMembers.length;
    toast({
      status: 'success',
      title: `${count} ${count === 1 ? 'user' : 'users'} added`,
      duration: 5000,
      isClosable: true,
    });
    setTimeout(() => onClose(), 5000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW='448px' w='90vw'>
        <ModalHeader px={6} py={4}>
          Invite your team
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={6} pt={2} pb={4}>
          <Alert status='success' mb={4} borderRadius='md'>
            <AlertIcon />
            Your site is on the way! It will appear in your Apps section.
          </Alert>
          <FormControl isRequired>
            <FormLabel>Add members</FormLabel>
            <InviteMembersDropdown
              onMembersSelect={setSelectedMembers}
              selected={selectedMembers}
            />
            <Text fontSize='xs' color='gray.600' mt={2}>
              The added members will be granted XMC Admin role for this
              environment.
            </Text>
          </FormControl>
        </ModalBody>
        <ModalFooter px={6} py={4}>
          <Tooltip
            label='Initializing your project, please wait.'
            isDisabled={!isGiveAccessDisabled}
            shouldWrapChildren
          >
            <Button
              colorScheme='purple'
              onClick={handleSend}
              isDisabled={isGiveAccessDisabled}
            >
              Give access
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(InviteTeamModal);
